import m from "mithril"
import { MithrilTsxComponent } from "mithril-tsx-component"
import * as State from "../../editor/state"
import { cleanString } from "oj-utils"
import { IHolder, ISide, IRect, IProduct } from "../types"
import "./preview.scss"
import { Icon } from "../../../components/Icon"
import { Loader } from "../../../components/Loader"
import { draggableAttrs, animateRemove, animateClassNames, animateCreate } from "oj-mithril-utils"
import { closest } from "oj-dom-utils"

let scale = 1
const updatePreviewScale = (w: number, h: number, mw: number, mh: number, container = document.body) =>
  scale = Math.min((container.clientWidth - mw) / w, (container.clientHeight - mh) / h)

const stopEvent = (fn: Function) => {
  return (...args) => {
    args[0].preventDefault()
    args[0].stopPropagation()
    return fn(...args)
  }
}

class Holder extends MithrilTsxComponent<IHolder & { index: number, active: boolean }> {
  dom: HTMLDivElement
  holder: IHolder
  position: IRect
  draggable: object
  resizableBottomRight: object
  resizableBottomLeft: object
  resizableTopRight: object
  resizableTopLeft: object

  oninit(v) {
    this.holder = State.getHolder()

    this.draggable = draggableAttrs({
      start: () => {
        this.setHolder(v.attrs.index)
        this.position = { ...this.holder.position }
      },
      drag: p => {
        State.setPosition({
          x: Math.round(this.position.x + (p.x * (1 / scale))),
          y: Math.round(this.position.y + (p.y * (1 / scale)))
        }, "move")
        m.redraw()
      },
      stop: () => {

      }
    })
    this.resizableBottomRight = draggableAttrs({
      start: () => {
        this.setHolder(v.attrs.index)
        this.position = { ...this.holder.position }
      },
      drag: p => {
        const pos: any = {}
        pos.width = Math.round(this.position.width + (p.x * (1 / scale)))
        pos.height = Math.round(this.position.height + (p.y * (1 / scale)))
        State.setPosition(pos, "resize-bottom-right")
        m.redraw()
      },
      stop: () => {

      }
    })
    this.resizableBottomLeft = draggableAttrs({
      start: () => {
        this.setHolder(v.attrs.index)
        this.position = { ...this.holder.position }
      },
      drag: p => {
        const pos: any = {}
        const diffX = p.x * (1 / scale)
        pos.x = Math.round(this.position.x + diffX)
        pos.width = Math.round(this.position.width - diffX)
        pos.height = Math.round(this.position.height + (p.y * (1 / scale)))
        State.setPosition(pos, "resize-bottom-left")
        m.redraw()
      },
      stop: () => {

      }
    })
    this.resizableTopRight = draggableAttrs({
      start: () => {
        this.setHolder(v.attrs.index)
        this.position = { ...this.holder.position }
      },
      drag: p => {
        const pos: any = {}
        const diffY = p.y * (1 / scale)
        pos.width = Math.round(this.position.width + (p.x * (1 / scale)))
        pos.y = Math.round(this.position.y + diffY)
        pos.height = Math.round(this.position.height - diffY)
        State.setPosition(pos, "resize-top-right")
        m.redraw()
      },
      stop: () => {

      }
    })
    this.resizableTopLeft = draggableAttrs({
      start: () => {
        this.setHolder(v.attrs.index)
        this.position = { ...this.holder.position }
      },
      drag: p => {
        const pos: any = {}
        const diffX = p.x * (1 / scale)
        const diffY = p.y * (1 / scale)
        pos.x = Math.round(this.position.x + diffX)
        pos.width = Math.round(this.position.width - diffX)
        pos.y = Math.round(this.position.y + diffY)
        pos.height = Math.round(this.position.height - diffY)
        State.setPosition(pos, "resize-top-left")
        m.redraw()
      },
      stop: () => {

      }
    })
  }

  onbeforeupdate(v, o) {
    this.holder = State.getHolder()
  }

  view(v) {
    const { type, position, active, index, actions } = v.attrs as IHolder & { active: boolean, index: number }

    const x = position.x * scale
    const y = position.y * scale
    const width = position.width * scale
    const height = position.height * scale
    const pending = type === "image" && State.getAction("image", v.attrs)?.value === "PENDING"

    return <div
      className={cleanString("Holder", `-${type}`, active && "active")}
      style={{ left: `${x}px`, top: `${y}px`, width: `${width}px`, height: `${height}px` }}
    >
      {pending && <Loader />}
      <div className="move" onclick={e => {
        this.setHolder(index)
        if (!this.holder.actions[0].value)
          this.emit()
      }} {...this.draggable} title="move" />
      {this.holder && [
        <div className="handle resize">
          <div {...this.resizableTopLeft} title="resize top left"><Icon icon="chevron_right" /></div>
          <div {...this.resizableTopRight} title="resize top right"><Icon icon="chevron_right" /></div>
          <div {...this.resizableBottomRight} title="resize bottom right"><Icon icon="chevron_right" /></div>
          <div {...this.resizableBottomLeft} title="resize bottom left"><Icon icon="chevron_right" /></div>
        </div>,
        <div className="handle rotate">
          <div onclick={stopEvent(e => State.setRotation(1, this.holder))} title="rotate right"><Icon icon="rotate_right" /></div>
        </div>
      ]}
    </div>
  }

  oncreate(v) {
    this.dom = v.dom
  }

  setHolder(index: number) {
    this.holder = State.setHolder(index)
  }

  emit() {
    State.ea.emit("setHolder", this.holder)
  }
}

class CanvasRender extends MithrilTsxComponent<{ side: ISide, removeAnimation: Function, onloaded: () => void }> {
  loaded: boolean

  oninit(v) {
    v.attrs.removeAnimation(() => animateRemove(v))
  }

  view(v) {
    return <div className={cleanString("CanvasRender", animateClassNames(v, v.attrs.side.key === "front" ? "flip-left" : "flip-right"))}>
      {this.loaded && <div className="shadow" />}
      <canvas oncreate={e =>
        State.setRenderer(e.dom as HTMLCanvasElement, v.attrs.side)
          .then(() => {
            this.loaded = true
            animateCreate(v)
            v.attrs.onloaded()
            m.redraw()
          })
      } />
    </div>
  }
}

class Product extends MithrilTsxComponent<{ side: ISide, container: HTMLElement }> {
  flipOutAnimation: Function
  loaded: boolean
  timer: any

  oninit(v) {
    this.timer = setTimeout(() => {
      if (this.loaded === undefined) {
        this.loaded = false
        m.redraw()
      }
    }, 200)
  }

  onremove(v) {
    clearTimeout(this.timer)
  }

  view(v) {
    const { side, container } = v.attrs as { side: ISide, container: HTMLElement }

    let width = side.position.width
    let height = side.position.height
    const scale = updatePreviewScale(width, height, 20, 20, container)
    width *= scale
    height *= scale

    return <div className="Product" style={{ width: `${width}px`, height: `${height}px` }}>
      {this.loaded === false && <Loader />}
      <CanvasRender side={side} removeAnimation={x => this.flipOutAnimation = x} onloaded={() => this.loaded = true} />
      {side.holders
        .map((x, i) =>
          <Holder {...x} index={i} active={i === side.holderIndex} />
        )}
    </div>
  }

  onbeforeremove(v, o) {
    this.flipOutAnimation()
    return new Promise(r => setTimeout(r, 100))
  }
}

export class Preview extends MithrilTsxComponent<{}> {
  side: ISide
  timer: any
  product: IProduct
  container: HTMLElement

  oninit(v) {
    this.product = State.getProduct()
    this.side = State.getSide()
  }

  view(v) {
    const zoom = State.getZoom()
    const holder = State.getHolder() && !zoom

    return <div className={cleanString("Preview", zoom && "zoom", holder && "holder-selected")}>
      {this.container && this.side && <Product side={this.side} container={this.container} />}
    </div>
  }

  oncreate(v) {
    this.container = v.dom
    window.on(["mousedown.Preview", "touchstart.Preview"], e => {
      if (closest(e.target, ".Holder") || !closest(e.target, ".Preview"))
        return

      e.preventDefault()
      e.stopPropagation()

      State.setHolder(undefined)
      m.redraw()
    })

    setTimeout(() => m.redraw(), 20)
  }

  onbeforeupdate(v, o) {
    const product = State.getProduct()
    const side = State.getSide()

    if (this.product === product && this.side === side)
      return

    this.side = undefined
    clearTimeout(this.timer)
    this.timer = setTimeout(() => {
      this.product = product
      this.side = side
      m.redraw()
    }, 100)
  }
}